// 🔧 settings_content.js
// ✅ Purpose: Admin-only Event Content page
// ✅ Features:
// - Standalone replacement for old Public Info tab
// - Event description
// - Participation tips
// - Optional content sections
// - Sponsor notes
// - Feedback notes
// - Loads/saves /events/{eventId}/settings/voting
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { logger } from "../../utils/logger";

const DEFAULT_PARTICIPATION_AND_VOTING_TIPS = `
  <p><strong>Participation and Voting Tips</strong></p>
  <ul>
    <li>Review the event details, categories, and entries before voting.</li>
    <li>To vote, find an entry you would like to vote for and scan the QR code displayed with that entry.</li>
    <li>Log in or create an account if prompted.</li>
    <li>Review the entry details before submitting your vote.</li>
    <li>You can visit your ballot at any time to review your submitted votes.</li>
    <li>Voting may only be available during the official voting window for this event.</li>
  </ul>
`;

const SettingsContent = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [description, setDescription] = useState("");
  const [participationTips, setParticipationTips] = useState("");
  const [optionalDetails1, setOptionalDetails1] = useState("");
  const [optionalDetails2, setOptionalDetails2] = useState("");
  const [feedbackNote, setFeedbackNote] = useState("");
  const [saving, setSaving] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      try {
        console.log("🎨 Loading event content settings for event:", eventId);

        const snap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const data = snap.exists()
          ? snap.data()
          : {};

        setSettings(data);

        setDescription(data.description || "");
        setParticipationTips(
          data.participationTips || DEFAULT_PARTICIPATION_AND_VOTING_TIPS
        );
        setOptionalDetails1(data.optionalDetails1 || "");
        setOptionalDetails2(data.optionalDetails2 || "");
        setFeedbackNote(data.feedbackNote || "");
      } catch (err) {
        logger.error("❌ Failed to load public info settings:", {
          value: err,
        });

        setStatus({
          type: "error",
          message: "Failed to load event content.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [eventId]);

  const handleSave = async () => {
    if (!eventId) return;

    setSaving(true);

    try {
      const update = {
        description,
        participationTips,
        optionalDetails1,
        optionalDetails2,
        feedbackNote,
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Public information saved successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed to save public info settings:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save public information.",
      });
    } finally {
      setSaving(false);
    }
  };

  const contentCount = [
    description,
    participationTips,
    optionalDetails1,
    optionalDetails2,
    feedbackNote,
  ].filter((value) => String(value || "").trim()).length;

  const lastSavedText = settings?.lastSaved?.toDate?.()
    ? settings.lastSaved.toDate().toLocaleString()
    : "Never";
    
  if (
    !ready ||
    eventLoading ||
    settingsLoading ||
    isManager === null
  ) {
    return <FullPageSpinner message="Loading content settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">
              🎨 Event Content
            </h1>

            <p className="lead text-muted mb-0">
              Configure public-facing event descriptions, participation guidance,
              optional content sections, and attendee messaging.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Event Content"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">
                  Content Sections Complete
                </div>

                <div className="fs-3 fw-bold">
                  {contentCount}/5
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Content Overview</strong>
          </div>

          <div className="card-body">
            <p className="text-muted mb-0">
              These sections appear on public event pages and help attendees,
              participants, sponsors, and volunteers understand your event.
            </p>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📝 Event Description</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={description}
              onChange={setDescription}
            />
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📢 Participation and Voting Tips</strong>
          </div>

          <div className="card-body">
            <p className="text-muted small">
              This section explains how attendees participate, vote, scan QR codes,
              and review their ballot.
            </p>

            <ReactQuill
              theme="snow"
              value={participationTips}
              onChange={setParticipationTips}
            />

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm mt-3"
              onClick={() =>
                setParticipationTips(
                  DEFAULT_PARTICIPATION_AND_VOTING_TIPS
                )
              }
            >
              ↩️ Reset to Default Tips
            </button>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📌 Optional Section 1</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={optionalDetails1}
              onChange={setOptionalDetails1}
            />
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📌 Optional Section 2</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={optionalDetails2}
              onChange={setOptionalDetails2}
            />
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📝 Feedback Note</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={feedbackNote}
              onChange={setFeedbackNote}
            />

            <div className="form-text mt-3">
              Appears wherever attendee feedback or follow-up communication is requested.
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Event Content</strong>

              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Event Content"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

};

export default SettingsContent;